import { source, stripIndent } from "common-tags";
import type { JSONSchema4 } from "json-schema";
import { compile } from "json-schema-to-typescript";
import { getTypesOutputPath } from "@/core/config.js";
import { TypeGenerationError } from "@/core/errors.js";
import type { AgentConfig } from "@/core/resources/agent/index.js";
import type { ConnectorResource } from "@/core/resources/connector/index.js";
import type { Entity } from "@/core/resources/entity/index.js";
import type { BackendFunction } from "@/core/resources/function/index.js";
import { writeFile } from "@/core/utils/fs.js";

interface GenerateTypesInput {
  projectRoot: string;
  entities: Entity[];
  functions: BackendFunction[];
  agents: AgentConfig[];
  connectors: ConnectorResource[];
}

const HEADER = stripIndent`
  // Auto-generated by Base44 CLI - DO NOT EDIT
  // Regenerate with: base44 types generate
`;

const EMPTY_TEMPLATE = stripIndent`
  // Auto-generated by Base44 CLI - DO NOT EDIT
  // Regenerate with: base44 types
  //
  // No entities, functions, agents, or connectors found in project.
  // Add resources to base44/entities/, base44/functions/, base44/agents/, or base44/connectors/
  // and run \`base44 types generate\` again.

  declare module '@base44/sdk' {
    // No types to augment - add resources and regenerate
  }
`;

/**
 * Generate and write types.d.ts file.
 */
export async function generateTypesFile(
  input: GenerateTypesInput,
): Promise<void> {
  const content = await generateContent(input);
  await writeFile(getTypesOutputPath(input.projectRoot), content);
}

async function generateContent(input: GenerateTypesInput): Promise<string> {
  const { entities, functions, agents, connectors } = input;

  if (
    !entities.length &&
    !functions.length &&
    !agents.length &&
    !connectors.length
  ) {
    return EMPTY_TEMPLATE;
  }

  const entityInterfaces = await Promise.all(
    entities.map((e) => compileEntity(e)),
  );

  // Build registry entries
  const registryEntries: [string, string[]][] = [
    [
      "EntityTypeRegistry",
      entities.map((e) => `"${e.name}": ${toPascalCase(e.name)};`),
    ],
    ["FunctionNameRegistry", functions.map((f) => `"${f.name}": true;`)],
    ["AgentNameRegistry", agents.map((a) => `"${a.name}": true;`)],
    ["ConnectorTypeRegistry", connectors.map((c) => `"${c.type}": true;`)],
  ];

  // Generate registries (only for non-empty entries)
  const registries = registryEntries
    .filter(([, entries]) => entries.length > 0)
    .map(([name, entries]) => registry(name, entries));

  return [
    HEADER,
    entityInterfaces.join("\n\n"),
    source`
      declare module '@base44/sdk' {
        ${registries.join("\n\n")}
      }
    `,
  ]
    .filter(Boolean)
    .join("\n\n");
}

async function compileEntity(entity: Entity): Promise<string> {
  const { name, source: _source, ...schema } = entity;

  const jsonSchema = {
    ...schema,
    title: name,
    additionalProperties: false,
  } as JSONSchema4;

  try {
    const ts = await compile(jsonSchema, name, {
      bannerComment: "",
      additionalProperties: false,
      strictIndexSignatures: true,
    });
    return ts.trim();
  } catch (error) {
    throw new TypeGenerationError(
      `Failed to generate types for entity "${name}"`,
      name,
      error,
    );
  }
}

function registry(name: string, entries: string[]): string {
  return source`
    interface ${name} {
      ${entries.join("\n")}
    }
  `;
}

function toPascalCase(name: string): string {
  return name
    .split(/[-_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join("");
}
